<script setup lang="ts">
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useMutation } from '@tanstack/vue-query'
import { useGameState } from '@/stores/gameState'
import { useUiState } from '@/stores/uiState'
import { toast, ToastTypes } from '@/helpers/toast'

import { RadioGroupItem, RadioGroupRoot } from 'radix-vue'
import UiPanel from '@/components/shared/UiPanel.vue'
import UiButton from '@/components/shared/UiButton.vue'
import UiAvatar from '@/components/shared/UiAvatar.vue'
import ModalWrapper from './ModalWrapper.vue'

import { addBot } from '@/api/table/addBot'

import { BOTS_LIST } from '@/constants'

const { t } = useI18n()

const selectedBotType = ref()

const { roomId } = storeToRefs(useGameState())
const { seatIndex } = storeToRefs(useUiState())
const { modals } = useUiState()
const { isPending: isJoining, mutate: add } = useMutation({
  mutationFn: addBot,
  onError(error) {
    toast({
      message: t('Errors.label.Oops'),
      details: error.message,
      icon: ToastTypes.ERROR
    })
  }
})

async function submit() {
  if (!roomId.value || !selectedBotType.value) return

  const bot = BOTS_LIST.find((el) => (el.type = selectedBotType.value))

  await add({
    tableId: roomId.value,
    seat: seatIndex.value ?? undefined,
    type: bot!.type
  })

  seatIndex.value = null
  modals.addBot.close()
}

function closeModal() {
  seatIndex.value = null
  modals.addBot.close()
}
</script>

<template>
  <modal-wrapper>
    <ui-panel
      class="join-game__panel"
      :title="$t('Compete.label.AddAIBot')"
      tag="div"
      @close="closeModal"
    >
      <div class="join-game__welcome-text">
        {{ $t('Compete.message.PlayFreeOnlinePoker') }}
      </div>

      <radio-group-root
        class="flex flex-col gap-1"
        default-value="default"
        orientation="vertical"
        v-model="selectedBotType"
      >
        <radio-group-item
          v-for="bot in BOTS_LIST"
          class="group aria-checked:outline-highlight! relative flex w-full gap-[10px] rounded-md bg-[2C2D3E] px-3 py-2 text-left text-white disabled:pointer-events-none disabled:opacity-50 aria-checked:bg-[#393A51] aria-checked:text-white aria-checked:outline-1 aria-checked:outline-solid"
          :value="bot.type"
          :key="bot.type"
          :disabled="!bot.enabled"
        >
          <slot name="item-label" :item="bot">
            <div class="h-[34px] w-[34px]">
              <span
                class="bg-highlight absolute top-1 left-1 block h-[15px] rounded-sm px-[3px] text-[9px] leading-[16px] font-normal text-white"
                >{{ bot.tag }}</span
              >
              <ui-avatar v-if="bot.avatarId" :id="bot.avatarId" />
              <ui-avatar v-else :src="bot.avatar" />
            </div>
            <div>
              <p class="mb-1 text-[15px] font-semibold">{{ $t(`Bots.label.${bot.type}`) }}</p>
              <p
                class="text-3 group-hover:text-secondary group-aria-checked:text-secondary leading-4 font-normal transition-colors"
              >
                {{ $t(`Bots.message.${bot.type}`) }}
              </p>
            </div>
          </slot>
        </radio-group-item>
      </radio-group-root>

      <ui-button
        id="addBot"
        type="button"
        :disabled="isJoining || !selectedBotType"
        @click="submit"
        >{{ $t('Compete.button.AddBot') }}</ui-button
      >
    </ui-panel>
  </modal-wrapper>
</template>

<style lang="scss">
.join-game {
  &__welcome-text {
    text-align: center;
    font-weight: 400;
  }

  &__avatars {
    width: 100%;
    margin: 0 4px;
  }

  &__welcome-control {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
}
</style>
